import { ALERT_CLASSIFICATION } from "@/components/alerts/constants";

import {
  BulletinContact,
  BulletinEntity,
  BulletinFormValues,
  BulletinIncidentTime,
  BulletinLocation,
  EntityType,
} from "./types";

/**
 * Client-side ids for rows and entities. These never reach an API — they exist so React keys stay
 * stable across reorders and so media items can point at an entity.
 */
export const createId = (): string => crypto.randomUUID();

export const createIncidentTime = (): BulletinIncidentTime => ({
  id: createId(),
  timeType: "exact",
  startDate: null,
  endDate: null,
});

export const createLocation = (): BulletinLocation => ({
  id: createId(),
  input: null,
  resolved: null,
  classification: null,
  otherClassification: "",
  unit: "",
});

export const createContact = (): BulletinContact => ({
  id: createId(),
  name: "",
  email: "",
  phoneNumber: "",
  extension: "",
  badgeNumber: "",
});

/**
 * Builds an empty entity of the given type. Used both when the user adds one by hand and when the
 * bulletin type seeds a placeholder — the seed then overrides `involvement` and sets `autoAdded`.
 *
 * Switching an existing entity's type goes through this rather than mutating in place, which is
 * what keeps stale fields from surviving the switch.
 */
export const createEntity = (type: EntityType): BulletinEntity => {
  const base = {
    id: createId(),
    autoAdded: false,
    additionalDescription: "",
    otherInvolvement: "",
  };

  switch (type) {
    case "person":
      return {
        ...base,
        type: "person",
        involvement: null,
        firstName: "",
        lastName: "",
        dateOfBirth: null,
      };
    case "vehicle":
      return {
        ...base,
        type: "vehicle",
        involvement: null,
        make: "",
        makeOther: "",
        model: "",
        modelOther: "",
        colour: "",
        colourOther: "",
        vehicleType: "",
        vehicleTypeOther: "",
        licencePlate: "",
        jurisdiction: "",
      };
    case "item":
      return { ...base, type: "item", involvement: null, itemName: "" };
  }
};

/**
 * A blank bulletin. One incident time, one location and one contact row exist from the start —
 * the design has these sections self-manage their rows rather than showing an "Add another" button
 * on an empty list, and every row that exists is required.
 */
export const createEmptyBulletinForm = (): BulletinFormValues => ({
  policeService: null,
  bulletinType: null,
  occurrenceNumbers: [],
  operationalArea: null,

  associatedCrimes: [],
  incidentTimes: [createIncidentTime()],
  locations: [createLocation()],

  entities: [],

  media: [],
  pdf: null,

  description: "",
  descriptionFr: "",
  originalLanguage: "en",

  contacts: [createContact()],
  approvedBy: "",
  approvedByBadge: "",
  classification: ALERT_CLASSIFICATION.PROTECTED_A,
  issuedTime: null,
});
